import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Cookies from "js-cookie";
import { GraduationCap, Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { useAlert } from "../../constants/AlertProvider";

export default function Education() {
  const { candidateId: routeCandidateId } = useParams();
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    phone_number: "",
    address: "",
  });
  const [applicationId, setApplicationId] = useState(null);
  const [education, setEducation] = useState([]);
  const [isRecruiterView, setIsRecruiterView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const showAlert = useAlert();

  const formatDate = (dateStr) => {
    if (!dateStr) return "Present";
    const [year, month, day] = dateStr.split("-");
    const date = new Date(`${year}-${month}-${day}`);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  useEffect(() => {
    const fetchEducation = async () => {
      try {
        setIsLoading(true);
        const loggedInUserId = Cookies.get("id");
        const targetUserId = routeCandidateId || loggedInUserId;

        if (!targetUserId) {
          throw new Error("Target User ID is missing");
        }
        if (routeCandidateId) {
          setIsRecruiterView(true);
        }

        let userRes;
        if (isRecruiterView) {
          userRes = await axios.get(`/api/candidate-profile/${targetUserId}/`, {
            withCredentials: true,
          });
        } else {
          userRes = await axios.get(`/api/users/${targetUserId}/`, {
            withCredentials: true,
          });
        }
        const userProfileData = userRes.data;
        setUserData(userProfileData);

        if (
          userProfileData.applicationids &&
          userProfileData.applicationids.length > 0
        ) {
          const appId = userRes.data.applicationids[0].id;
          setApplicationId(appId);

          const eduResponse = await axios.get(
            `/api/education/?application_id=${appId}`,
            {
              headers: { "X-CSRFToken": Cookies.get("csrftoken") },
              withCredentials: true,
            },
          );

          if (eduResponse.data && Array.isArray(eduResponse.data.results)) {
            setEducation(eduResponse.data.results);
          } else {
            throw new Error("Invalid API response format");
          }
        } else {
          setEducation([]);
        }
      } catch (error) {
        console.error("Error fetching education:", error);
        showAlert("Failed to fetch education.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchEducation();
  }, [showAlert, routeCandidateId, isRecruiterView]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircularProgress color="primary" size={30} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {education.length === 0 ? (
        <div className="text-center py-12 rounded-lg">
          <GraduationCap className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No Education Added
          </h3>
          {!isRecruiterView && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Your education will be displayed here. You can add it by editing
              your profile.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Education ({education.length})
            </h2>
          </div>

          {education.map((edu) => (
            <div
              key={edu.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4"
            >
              <div className="flex items-start space-x-4">
                <GraduationCap className="h-6 w-6 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100">
                    {edu.degree} in {edu.field_of_study}
                  </h3>
                  <p className="text-md text-gray-700 dark:text-gray-300">
                    {edu.institution}
                  </p>
                  <div className="mt-1 flex items-center gap-x-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1.5" />
                      {formatDate(edu.start_date)} - {formatDate(edu.end_date)}
                    </span>
                    {edu.gpa && (
                      <span className="font-semibold">GPA: {edu.gpa}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
